/**
 * Optional email sync during onboarding — import trips from booking confirmations.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { View, ActivityIndicator, ScrollView, Pressable, Alert, Platform, Modal, AppState } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import Constants from 'expo-constants';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { queueTripDetection } from '@/services/tripDetection';
import {
  checkInboxStatus,
  initiateInboxAuth,
  isInboxEmulatorMode,
  type InboxProviderId,
  syncInbox,
  type ExtractedBooking,
} from '@/services/inboxSync';
import { ImapSettingsModal } from '@/components/planning/ImapSettingsModal';
import type { DetectedTrip } from '@/services/tripDetection';
import { format } from 'date-fns';
import { useUserStore } from '@/state/userStore';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import { createTrip, type CreateTripData } from '@/services/trips';
import { auth, db } from '@/lib/firebase';
import { DetectedTripsPreview } from '@/components/trips/DetectedTripsPreview';
import { useToast } from '@/components/ui/ToastProvider';
import { logger } from '@/services/logger';
import {
  clearInboxOauthMarkers,
  getAllowedOauthOrigins,
  getInboxOauthPending,
  getInboxOauthSuccess,
  isTrustedOauthOrigin,
  setInboxOauthPending,
} from '@/utils/inboxOauth';
import { completeOAuthWebSession } from '@/utils/completeOAuthWebSession';
import { schedulePresentAfterAuthSessionDismiss } from '@/utils/schedulePresentAfterAuthSessionDismiss';
import { LoadingState } from '@/components/ui/LoadingState';
import { fromLocalDateKey } from '@/utils/dateKeys';
import { useAuthUid } from '@/hooks/useAuthUid';
import { withTimeout } from '@/utils/withTimeout';
import {
  emailScanLookbackExplanation,
  EMAIL_SYNC_ONBOARDING_LOOKBACK_DAYS,
} from '@/utils/emailScanLookbackCopy';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';
import { isTripLimitEntitlementError } from '@/services/entitlements';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { useOnboardingStepAnalytics } from '@/hooks/useOnboardingStepAnalytics';
import { markOnboardingComplete } from '@/services/onboardingCompletion';

const PROVIDER_LABELS: Record<InboxProviderId, string> = {
  gmail: 'Gmail',
  outlook: 'Outlook',
  icloud: 'iCloud',
};

const PROVIDER_ICONS: Record<InboxProviderId, keyof typeof Ionicons.glyphMap> = {
  gmail: 'mail-outline',
  outlook: 'mail-outline',
  icloud: 'cloud-outline',
};

const PROVIDER_DESCRIPTIONS: Record<InboxProviderId, string> = {
  gmail: 'Connect with Google OAuth',
  outlook: 'Connect with Microsoft OAuth',
  icloud: 'Connect with IMAP settings',
};

export default function EmailSyncOnboardingScreen() {
  const router = useRouter();
  const theme = useTheme();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const isNewUser = useUserStore((state) => state.isNewUser);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding);
  const authReady = useUserStore((state) => state.authReady);
  const { showSuccess, showError } = useToast();
  const openPremiumGate = usePremiumGateStore((s) => s.open);
  type DetectionPhase = 'idle' | 'running';
  type CreationPhase = 'idle' | 'running' | 'completed';
  const [syncFlow, setSyncFlow] = useState<{ detection: DetectionPhase; creation: CreationPhase }>({
    detection: 'idle',
    creation: 'idle',
  });
  const detecting = syncFlow.detection === 'running';
  const isDetectingTrips = syncFlow.detection === 'running';
  const creatingTrips = syncFlow.creation === 'running';
  const tripsCreated = syncFlow.creation === 'completed';
  const setDetecting = useCallback((next: boolean) => {
    setSyncFlow((prev) => ({ ...prev, detection: next ? 'running' : 'idle' }));
  }, []);
  const setIsDetectingTrips = useCallback((next: boolean) => {
    setSyncFlow((prev) => ({ ...prev, detection: next ? 'running' : 'idle' }));
  }, []);
  const setCreatingTrips = useCallback((next: boolean) => {
    setSyncFlow((prev) => ({ ...prev, creation: next ? 'running' : prev.creation === 'running' ? 'idle' : prev.creation }));
  }, []);
  const setTripsCreated = useCallback((next: boolean) => {
    setSyncFlow((prev) => ({ ...prev, creation: next ? 'completed' : prev.creation === 'completed' ? 'idle' : prev.creation }));
  }, []);
  const [connecting, setConnecting] = useState<InboxProviderId | null>(null);
  const [detectedTrips, setDetectedTrips] = useState<DetectedTrip[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasTriedDetection, setHasTriedDetection] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<InboxProviderId | null>(null);
  const [showProviderSelection, setShowProviderSelection] = useState(false);
  const [inboxConnections, setInboxConnections] = useState<Record<InboxProviderId, boolean | null>>({
    gmail: null,
    outlook: null,
    icloud: null,
  });
  const [imapModalVisible, setImapModalVisible] = useState(false);
  const [pendingProvider, setPendingProvider] = useState<InboxProviderId | null>(null);
  const [detectionProgress, setDetectionProgress] = useState<{ progress: number; message: string } | null>(null);
  const [dismissedTrips, setDismissedTrips] = useState<Set<number>>(new Set());
  const [createdTripIds, setCreatedTripIds] = useState<string[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [creationProgress, setCreationProgress] = useState<{ current: number; total: number; tripName?: string } | null>(null);
  const [itinerarySyncStatus, setItinerarySyncStatus] = useState<Record<string, 'pending' | 'syncing' | 'complete'>>({});
  const [failedTrips, setFailedTrips] = useState<Array<{ trip: DetectedTrip; error: string }>>([]);
  const [resultsCollapsed, setResultsCollapsed] = useState(false);
  const [selectedTripsForMerge, setSelectedTripsForMerge] = useState<Set<number>>(new Set());
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [completingOnboarding, setCompletingOnboarding] = useState(false);
  const [showDetectedTripsPreview, setShowDetectedTripsPreview] = useState(false);
  const [minimizedDetectionSession, setMinimizedDetectionSession] = useState<{
    taskId: string;
    provider: InboxProviderId;
    label: string;
  } | null>(null);
  const authUid = useAuthUid();
  const { logCompleted, logSkipped } = useOnboardingStepAnalytics('email-sync');

  const useUnifiedDetectedTrips = true;
  
  // Track which providers have already triggered detection to prevent duplicate tasks
  const detectionTriggeredRef = useRef<Set<InboxProviderId>>(new Set());
  // Avoid double-opening preview when inline postMessage handler already opened it (web)
  const oauthPreviewOpenedRef = useRef(false);
  const cancelNativePreviewAfterOauthRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      cancelNativePreviewAfterOauthRef.current?.();
      cancelNativePreviewAfterOauthRef.current = null;
    };
  }, []);

  useEffect(() => {
    // Check connection status for all providers
    checkAllProviderStatuses();
  }, []);

  // Listen for OAuth success messages from popup window (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleMessage = async (event: MessageEvent) => {
      // Only accept messages from our OAuth callback (staging/prod aware)
      const projectId = (Constants.expoConfig?.extra as any)?.firebaseConfig?.projectId || process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID;
      const allowed = getAllowedOauthOrigins(projectId);
      if (!isTrustedOauthOrigin(event.origin, allowed)) {
        return;
      }

      if (event.data?.type === 'gmail-oauth-success' || event.data?.type === 'outlook-oauth-success') {
        console.log('[EmailSyncOnboarding] OAuth success message received');
        
        // Determine which provider succeeded from the message type
        const connectedProvider: InboxProviderId = event.data?.type === 'gmail-oauth-success' ? 'gmail' : 'outlook';
        
        console.log(`[EmailSyncOnboarding] OAuth success for ${connectedProvider}, checking status from production Firestore...`);
        
        // In emulator mode, the OAuth callback may store tokens in production while the app is
        // watching emulator Firestore. Use a short retry loop to bridge that.
        const useRetry = isInboxEmulatorMode();
        const maxAttempts = useRetry ? 12 : 3;
        const checkStatusWithRetry = async (attempt: number = 1) => {
          try {
            console.log(`[EmailSyncOnboarding] Checking ${connectedProvider} status (attempt ${attempt}/${maxAttempts})...`, {
              emulatorMode: useRetry,
            });
            const status = await checkInboxStatus(connectedProvider);
            console.log(`[EmailSyncOnboarding] ${connectedProvider} status check result:`, {
              connected: status.connected,
              requiresAuth: status.requiresAuth,
              hasLastConnectedAt: !!status.lastConnectedAt,
            });
            
            if (status.connected) {
              console.log(`[EmailSyncOnboarding] ${connectedProvider} connection verified on attempt ${attempt}`);
              setInboxConnections(prev => ({ ...prev, [connectedProvider]: true }));
              setConnecting(null);
              if (useUnifiedDetectedTrips) {
                if (!oauthPreviewOpenedRef.current) {
                  setSelectedProvider(connectedProvider);
                  setShowDetectedTripsPreview(true);
                }
              } else {
                // Trigger trip detection
                await checkForExistingTrips(connectedProvider);
              }
            } else if (attempt < maxAttempts) {
              // Retry after a delay (short, capped backoff). Embedded browsers + token propagation
              // can cause a small delay before status flips to connected.
              const delayMs = Math.min(800 + (attempt - 1) * 600, 4000);
              console.log(
                `[EmailSyncOnboarding] ${connectedProvider} not connected yet, retrying in ${delayMs}ms (attempt ${attempt}/${maxAttempts})...`,
              );
              setTimeout(() => checkStatusWithRetry(attempt + 1), delayMs);
            } else {
              console.warn(`[EmailSyncOnboarding] ${connectedProvider} connection not detected after ${maxAttempts} attempts`);
              setConnecting(null);
              // Still update UI to show connection might be pending
              setInboxConnections(prev => ({ ...prev, [connectedProvider]: true }));
              setError(`Connection verified but status check failed. Please refresh the page.`);
            }
          } catch (error) {
            console.error(`[EmailSyncOnboarding] Error checking ${connectedProvider} status (attempt ${attempt}):`, error);
            if (attempt < maxAttempts) {
              const delayMs = Math.min(800 + (attempt - 1) * 600, 4000);
              setTimeout(() => checkStatusWithRetry(attempt + 1), delayMs);
            } else {
              console.error(`[EmailSyncOnboarding] Failed to verify ${connectedProvider} connection after ${maxAttempts} attempts`);
              setConnecting(null);
              setError(`Failed to verify connection. Please try refreshing the page.`);
            }
          }
        };
        
        // Start quickly; only emulator mode needs a bit more patience.
        const initialDelayMs = useRetry ? 1500 : 200;
        console.log(`[EmailSyncOnboarding] Scheduling first status check in ${initialDelayMs}ms`);
        setTimeout(() => checkStatusWithRetry(), initialDelayMs);
      } else if (event.data?.type === 'gmail-oauth-error' || event.data?.type === 'outlook-oauth-error') {
        console.error('[EmailSyncOnboarding] OAuth error:', event.data.error);
        setError('We couldn’t connect your inbox. Please try again.');
        setConnecting(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider]); // checkForExistingTrips is stable (useCallback) and called inside handler

  const resumeInboxAfterOauth = useCallback(async (provider: InboxProviderId) => {
    // Brief delay so OAuth callback can persist tokens before we poll (Safari / in-app browser return).
    await new Promise((r) => setTimeout(r, 500));
    for (let attempt = 0; attempt < 12; attempt++) {
      if (!auth.currentUser?.uid) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 400));
        continue;
      }
      const status = await checkInboxStatus(provider, { bypassCache: true });
      if (status?.connected) {
        setInboxConnections((prev) => ({ ...prev, [provider]: true }));
        setConnecting(null);
        setSelectedProvider(provider);
        detectionTriggeredRef.current.add(provider);
        await clearInboxOauthMarkers();
        if (Platform.OS === 'web') {
          setShowDetectedTripsPreview(true);
        } else {
          cancelNativePreviewAfterOauthRef.current?.();
          cancelNativePreviewAfterOauthRef.current = schedulePresentAfterAuthSessionDismiss(() => {
            cancelNativePreviewAfterOauthRef.current = null;
            completeOAuthWebSession();
            setShowDetectedTripsPreview(true);
          });
        }
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, Math.min(800 + attempt * 350, 2800)));
    }
    setConnecting(null);
  }, []);

  // Auto-resume after OAuth: AppState + auth hydration (Zustand can lag behind Firebase after Safari / ASWebAuthenticationSession).
  useEffect(() => {
    const run = async () => {
      try {
        if (!auth.currentUser?.uid) return;
        const success = await getInboxOauthSuccess();
        const pending = success ? null : await getInboxOauthPending();
        const provider = (success?.provider || pending?.provider) as InboxProviderId | undefined;
        if (!provider) return;
        await resumeInboxAfterOauth(provider);
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        logger.warn('[EmailSync] OAuth resume failed', undefined, err);
        try {
          showError('We could not finish connecting your inbox. Please try again.');
        } catch {
          /* toast may not be ready during boot */
        }
      }
    };

    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') void run();
    });
    void run();

    const unsubAuth = onAuthStateChanged(auth, () => {
      void run();
    });

    if (Platform.OS === 'web') {
      const onVisibility = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') void run();
      };
      document.addEventListener('visibilitychange', onVisibility);
      return () => {
        sub.remove();
        unsubAuth();
        document.removeEventListener('visibilitychange', onVisibility);
      };
    }
    return () => {
      sub.remove();
      unsubAuth();
    };
  }, [resumeInboxAfterOauth]);

  const checkAllProviderStatuses = async () => {
    const providers: InboxProviderId[] = ['gmail', 'outlook', 'icloud'];
    const statuses: Record<InboxProviderId, boolean | null> = {
      gmail: null,
      outlook: null,
      icloud: null,
    };

    for (const provider of providers) {
      try {
        const status = await checkInboxStatus(provider);
        statuses[provider] = status.connected ?? false;
      } catch (err) {
        // Only log warning, don't set to false - this prevents iCloud errors from showing when checking Gmail
        // iCloud may timeout if not configured, which is expected
        console.warn(`[EmailSyncOnboarding] Error checking ${provider} status (this is OK if provider is not connected):`, err);
        // Don't set statuses[provider] = false here - leave it as null to indicate "unknown"
      }
    }

    setInboxConnections(statuses);
  };

  const checkForExistingTrips = useCallback(async (provider: InboxProviderId = 'gmail') => {
    // Prevent multiple simultaneous detection attempts
    if (isDetectingTrips) {
      console.log('[EmailSyncOnboarding] Detection already in progress, skipping...');
      return;
    }
    
    // Prevent duplicate detection for the same provider
    if (detectionTriggeredRef.current.has(provider)) {
      console.log(`[EmailSyncOnboarding] Detection already triggered for ${provider}, skipping...`);
      return;
    }
    
    try {
      // Mark as triggered immediately to prevent race conditions
      detectionTriggeredRef.current.add(provider);
      setIsDetectingTrips(true);
      setDetecting(true);
      setError(null);
      setDetectionProgress({ progress: 0, message: 'Initializing...' }); // Reset progress
      // Mark as tried immediately to prevent loops
      setHasTriedDetection(true);
      
      // Use async queue for better UX (non-blocking)
      // Update progress immediately to show we're queuing
      setDetectionProgress({ progress: 5, message: 'Queuing detection task...' });
      
      const { taskId, unsubscribe } = await queueTripDetection(
        // Scan more than the “top 20” matching emails; otherwise legitimate bookings (e.g. from a few days ago)
        // can be pushed out by newer “booking-ish” emails.
        { provider, maxResults: 120, lookbackDays: EMAIL_SYNC_ONBOARDING_LOOKBACK_DAYS },
        (status) => {
          console.log('[EmailSyncOnboarding] Detection status update:', {
            status: status.status,
            progress: status.progress,
            message: status.message,
            tripsCount: status.trips?.length || 0,
            totalBookings: status.totalBookings,
          });
          
          // Update UI based on progress
          if (status.status === 'processing') {
            setDetecting(true);
            // Update progress state for UI display
            setDetectionProgress({
              progress: status.progress || 0,
              message: status.message || 'Processing...',
            });
            // Update progress message for better UX
            if (status.message) {
              console.log(`[EmailSyncOnboarding] Progress: ${status.progress}% - ${status.message}`);
            }
          } else if (status.status === 'completed') {
            const trips = status.trips || [];
            void logAnalyticsEvent('email_sync_sync_completed', {
              provider,
              source: 'onboarding',
              result: 'success',
              bookingsCount: status.totalBookings,
              tripsCreated: 0,
            });
            console.log('[EmailSyncOnboarding] ✅ Detection completed:', {
              tripsFound: trips.length,
              totalBookings: status.totalBookings,
              trips: trips.map(t => ({ name: t.suggestedName, location: t.location, dates: `${t.startDate} - ${t.endDate}` })),
            });
            // Remove duplicates before setting detected trips
            // Use a combination of name, dates, and location to identify duplicates
            const uniqueTrips = trips.filter((trip, index, self) => {
              return index === self.findIndex(t => 
                t.suggestedName === trip.suggestedName &&
                t.startDate === trip.startDate &&
                t.endDate === trip.endDate &&
                t.location === trip.location
              );
            });
            console.log(`[EmailSyncOnboarding] Filtered ${trips.length} trips to ${uniqueTrips.length} unique trips`);
            setDetectedTrips(uniqueTrips);
            setDetecting(false);
            setIsDetectingTrips(false);
            setDetectionProgress(null); // Clear progress when complete
            unsubscribe();
            
            // Show user-friendly message based on results
            if (trips.length === 0) {
              // Check if we have diagnostic info to provide better error message
              if (status.diagnosticInfo?.messagesFound === 0) {
                setError('No emails found in your inbox. Try checking your Spam folder or adjusting the date range.');
              } else if (status.diagnosticInfo?.bookingsExtracted === 0 && status.diagnosticInfo?.messagesFound && status.diagnosticInfo.messagesFound > 0) {
                setError(`Found ${status.diagnosticInfo.messagesFound} email(s) but couldn't extract any booking information. The emails may not contain trip confirmations.`);
              } else {
                setError('No trips found in your email. Try checking your Spam folder or adjusting the date range.');
              }
            } else {
              // Clear any previous errors on success
              setError(null);
              // Note: Auto-creation will be handled by useEffect watching detectedTrips
            }
          } else if (status.status === 'failed') {
            console.error('[EmailSyncOnboarding] Detection failed:', status.error, status.errorCode);
            void logAnalyticsEvent('email_sync_sync_completed', {
              provider,
              source: 'onboarding',
              result: 'failed',
              bookingsCount: status.totalBookings,
              tripsCreated: 0,
              error: status.error || status.errorCode || 'unknown',
            });
            
            // Handle specific error codes with user-friendly messages
            let errorMessage = status.error || 'Failed to detect trips. Please try again.';
            
            if (status.errorCode === 'AUTH_PENDING' || status.error?.includes('not connected') || status.error?.includes('Authentication required')) {
              errorMessage = 'Email connection is still being verified. Please wait a moment and try again.';
            } else if (status.errorCode === 'NO_EMAILS_FOUND') {
              errorMessage = 'No emails found in your inbox. Try checking your Spam folder or adjusting the date range.';
            } else if (status.errorCode === 'NO_BOOKINGS_EXTRACTED') {
              const msgCount = status.diagnosticInfo?.messagesFound || 0;
              errorMessage = msgCount > 0 
                ? `Found ${msgCount} email(s) but couldn't extract any booking information. The emails may not contain trip confirmations.`
                : 'Found emails but couldn\'t extract booking information. The emails may not contain trip confirmations.';
            }
            
            setError(errorMessage);
            setDetecting(false);
            setIsDetectingTrips(false);
            setDetectionProgress(null); // Clear progress on error
            unsubscribe();
          } else if (status.status === 'queued') {
            setDetecting(true);
            // Keep showing loading while queued
          }
        }
      );
      
      // Note: Progress updates are handled via the onProgress callback above
      // The unsubscribe function will be called when status is 'completed' or 'failed'
      
    } catch (err) {
      void logAnalyticsEvent('email_sync_sync_completed', {
        provider,
        source: 'onboarding',
        result: 'failed',
        error: err instanceof Error ? err.message : String(err),
      });
      // If auth is required, that's expected - user needs to connect
      if (err instanceof Error && err.message.includes('Authentication required')) {
        setIsDetectingTrips(false);
        return;
      }
      
      // Handle CORS and connection errors with helpful messages
      if (err instanceof Error) {
        const errorMessage = err.message.toLowerCase();
        if (errorMessage.includes('cors') || errorMessage.includes('failed to fetch') || errorMessage.includes('connection refused')) {
          setError('Unable to connect right now. Check your internet connection and try again.');
        } else if (errorMessage.includes('internal')) {
          setError('The server is having trouble right now. Please try again.');
        } else {
          setError(humanizeAsyncError(err, 'We couldn’t scan your inbox right now. Please try again.'));
        }
      } else {
        setError('We couldn’t scan your inbox right now. Please try again.');
      }
      
      console.error('[EmailSyncOnboarding] Error detecting trips:', err);
      setDetecting(false);
      setIsDetectingTrips(false);
    }
  }, [isDetectingTrips, setIsDetectingTrips, setDetecting, setError, setHasTriedDetection, setDetectedTrips]);

  // Web: reflect inbox connection flags from Firestore only (trip detection starts only from explicit UI / OAuth handlers).
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!firebaseUser?.uid) return;

    console.log('[EmailSyncOnboarding] Setting up real-time Firestore listener');

    const userDocRef = doc(db, 'users', firebaseUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
      const userData = docSnapshot.data();
      if (!userData) return;

      if (userData.gmailAccessToken && userData.gmailRefreshToken) {
        setInboxConnections((prev) => (prev.gmail === true ? prev : { ...prev, gmail: true }));
      }
      if (userData.graphAccessToken && userData.graphRefreshToken) {
        setInboxConnections((prev) => (prev.outlook === true ? prev : { ...prev, outlook: true }));
      }
      if (userData.imapSettings) {
        setInboxConnections((prev) => (prev.icloud === true ? prev : { ...prev, icloud: true }));
      }
    }, (error) => {
      console.error('[EmailSyncOnboarding] Error in real-time listener:', error);
      const message = humanizeAsyncError(
        error,
        'Live sync was interrupted. Please reconnect your inbox or try again.',
      );
      setError(message);
      showError(message);
    });

    return () => {
      console.log('[EmailSyncOnboarding] Cleaning up real-time listener');
      unsubscribe();
    };
  }, [firebaseUser?.uid]);

  const handleSelectProvider = async (provider: InboxProviderId) => {
    let waitingForOauthCallback = false;
    setSelectedProvider(provider);
    setShowProviderSelection(false);
    setConnecting(provider);
    setError(null);
    void logAnalyticsEvent('email_sync_connect_started', {
      provider,
      source: 'onboarding',
    });

    try {
      // Web UX: open a placeholder popup immediately on click (before any awaits).
      // This avoids popup blockers and makes the window feel instant even if authUrl fetch is slow.
      const isOauthProvider = provider === 'gmail' || provider === 'outlook';
      const popup: Window | null =
        Platform.OS === 'web' && isOauthProvider
          ? (window as any).open('about:blank', 'oauth', 'width=600,height=700')
          : null;

      if (Platform.OS === 'web' && isOauthProvider) {
        if (!popup) {
          Alert.alert('Popup blocked', 'Please allow popups for this site to complete authentication.');
          setConnecting(null);
          return;
        }
        try {
          popup.document.title = `Connecting ${PROVIDER_LABELS[provider]}…`;
          popup.document.body.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
          popup.document.body.style.padding = '24px';
          popup.document.body.innerHTML = `
            <div style="max-width:520px;margin:0 auto;">
              <h2 style="margin:0 0 8px 0;">Connecting ${PROVIDER_LABELS[provider]}…</h2>
              <p style="margin:0;color:#555;line-height:1.4;">
                This window will update automatically. If nothing happens, check your popup settings and try again.
              </p>
            </div>
          `;
        } catch {
          // If document access is restricted for any reason, ignore and continue.
        }
      }

      // Check connection status
      const status = await checkInboxStatus(provider);
      
      // Handle iCloud/IMAP - show settings modal if not connected
      if (provider === 'icloud' && (!status || !status.connected)) {
        setPendingProvider(provider);
        setImapModalVisible(true);
        setConnecting(null);
        return;
      }

      // If requires auth, initiate authentication
      if (status?.requiresAuth || !status?.connected) {
        console.log('[EmailSyncOnboarding] Inbox requires auth, launching OAuth', { provider });
        const authResponse = await initiateInboxAuth(provider);
        if (authResponse?.authUrl) {
          waitingForOauthCallback = true;
          await setInboxOauthPending(provider).catch(() => {});
          if (Platform.OS === 'web') {
              // We already opened a placeholder popup synchronously. Redirect it now.
              try {
                popup?.location.assign(authResponse.authUrl);
              } catch {
                // Fallback if assign fails (rare)
                (window as any).open(authResponse.authUrl, 'oauth', 'width=600,height=700');
              }

              // Use postMessage instead of polling window.closed to avoid COOP errors
              let timeoutId: NodeJS.Timeout | null = null;
              
              const messageHandler = async (event: MessageEvent) => {
                // Verify origin for security (staging/prod aware)
                const projectId =
                  (Constants.expoConfig?.extra as any)?.firebaseConfig?.projectId ||
                  process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID;
                const allowedOrigins = getAllowedOauthOrigins(projectId);

                if (!isTrustedOauthOrigin(event.origin, allowedOrigins)) {
                  return; // Ignore messages from untrusted origins
                }
                
                // Handle OAuth success/error messages
                if (event.data?.type === 'gmail-oauth-success' || event.data?.type === 'outlook-oauth-success') {
                  window.removeEventListener('message', messageHandler);
                  if (timeoutId) clearTimeout(timeoutId);
                  
                  // Determine which provider succeeded from the message type
                  const connectedProvider: InboxProviderId = event.data?.type === 'gmail-oauth-success' ? 'gmail' : 'outlook';
                  
                  // Open sync UI immediately so user sees syncing without clicking again
                  oauthPreviewOpenedRef.current = true;
                  setSelectedProvider(connectedProvider);
                  setShowDetectedTripsPreview(true);
                  setConnecting(null);
                  setInboxConnections(prev => ({ ...prev, [connectedProvider]: true }));
                  void logAnalyticsEvent('email_sync_connect_completed', {
                    provider: connectedProvider,
                    source: 'onboarding',
                    result: 'success',
                  });
                  detectionTriggeredRef.current.add(connectedProvider);
                  clearInboxOauthMarkers().catch(() => {});
                  console.log(`[EmailSyncOnboarding] OAuth success for ${connectedProvider}, opened sync preview.`);
                } else if (event.data?.type === 'gmail-oauth-error' || event.data?.type === 'outlook-oauth-error') {
                  window.removeEventListener('message', messageHandler);
                  if (timeoutId) clearTimeout(timeoutId);
                  const errorMsg = event.data?.error || 'Authentication failed';
                  console.error('[EmailSyncOnboarding] OAuth error:', errorMsg);
                  void logAnalyticsEvent('email_sync_connect_completed', {
                    provider,
                    source: 'onboarding',
                    result: 'failed',
                    error: errorMsg,
                  });
                  setError('We couldn’t connect your inbox. Please try again.');
                  setConnecting(null);
                }
              };
              
              window.addEventListener('message', messageHandler);
              
              // Fallback: Verify connection after timeout even if no message received
              timeoutId = setTimeout(async () => {
                window.removeEventListener('message', messageHandler);
                // Real-time listener will automatically detect when tokens are stored
                // No need to poll - just wait for the listener to trigger
                console.log('[EmailSyncOnboarding] OAuth initiated, waiting for listener to detect tokens');
                setConnecting(null);
              }, 30000); // 30 second timeout
          } else {
            const canOpen = await Linking.canOpenURL(authResponse.authUrl);
            if (canOpen) {
              const redirectUrl = Linking.createURL(
                provider === 'gmail' ? 'gmail-callback' : 'outlook-callback',
              );
              const result = await WebBrowser.openAuthSessionAsync(authResponse.authUrl, redirectUrl);
              completeOAuthWebSession();
              if (result.type !== 'success') {
                Alert.alert('Authentication cancelled', 'Return to Tripvio and try connecting again.');
                void logAnalyticsEvent('email_sync_connect_completed', {
                  provider,
                  source: 'onboarding',
                  result: 'failed',
                  error: 'cancelled',
                });
                setConnecting(null);
                return;
              }
              await resumeInboxAfterOauth(provider);
              void logAnalyticsEvent('email_sync_connect_completed', {
                provider,
                source: 'onboarding',
                result: 'success',
              });
            } else {
              Alert.alert('Connect inbox', 'Unable to open the authorization page automatically.');
            }
          }
        } else {
          waitingForOauthCallback = false;
          // If we opened a placeholder popup, close it since we couldn't start auth.
          try { popup?.close(); } catch {}
          Alert.alert('Connect inbox', authResponse?.message || 'Unable to start authorization for this provider.');
        }
        if (!waitingForOauthCallback) {
          setConnecting(null);
        }
        return;
      }

      // If connected, detect trips
      if (status?.connected) {
        void logAnalyticsEvent('email_sync_connect_completed', {
          provider,
          source: 'onboarding',
          result: 'success',
        });
        if (useUnifiedDetectedTrips) {
          setSelectedProvider(provider);
          setShowDetectedTripsPreview(true);
        } else {
          await checkForExistingTrips(provider);
        }
      }
    } catch (err) {
      waitingForOauthCallback = false;
      const message = err instanceof Error ? err.message : String(err);
      console.error('[EmailSyncOnboarding] Error connecting email', { message });
      void logAnalyticsEvent('email_sync_connect_completed', {
        provider,
        source: 'onboarding',
        result: 'failed',
        error: message,
      });
      setError(humanizeAsyncError(err, 'We couldn’t connect your inbox. Please try again.'));
      setConnecting(null);
    } finally {
      if (!waitingForOauthCallback) {
        setConnecting(null);
      }
    }
  };

  const handleImapSettingsSave = async () => {
    setImapModalVisible(false);
    if (pendingProvider) {
      void logAnalyticsEvent('email_sync_connect_completed', {
        provider: pendingProvider,
        source: 'onboarding',
        result: 'success',
      });
      // Real-time listener will automatically detect when IMAP settings are stored
      // No need to poll - just wait for the listener to trigger
      console.log(`[EmailSyncOnboarding] IMAP settings saved for ${pendingProvider}, waiting for real-time listener to detect connection...`);
      setPendingProvider(null);
    }
  };

  const finishOnboardingAndGoToTabs = useCallback(async () => {
    await markOnboardingComplete(firebaseUser?.uid);
    logCompleted();
    router.replace('/(tabs)');
  }, [firebaseUser?.uid, logCompleted, router]);

  const handleSkip = async () => {
    if (completingOnboarding) {
      return;
    }
    setCompletingOnboarding(true);
    logSkipped('user_skip');
    try {
      await finishOnboardingAndGoToTabs();
    } finally {
      setCompletingOnboarding(false);
    }
  };

  const handleCreateTrips = async () => {
    if (!firebaseUser?.uid || visibleTrips.length === 0) {
      return;
    }

    // Prevent duplicate creation if already in progress
    if (creatingTrips) {
      console.warn('[EmailSyncOnboarding] Trip creation already in progress, ignoring duplicate request');
      return;
    }

    setCreatingTrips(true);
    setError(null);
    setFailedTrips([]);
    setCreatedTripIds([]);
    setItinerarySyncStatus({});
    setResultsCollapsed(false);

    try {
      const provider = selectedProvider || 'gmail';
      let successCount = 0;
      let failCount = 0;
      const newTripIds: string[] = [];
      const newFailedTrips: Array<{ trip: DetectedTrip; error: string }> = [];
      let tripLimitReached = false;

      // Only create visible (non-dismissed) trips
      for (let i = 0; i < visibleTrips.length; i++) {
        const trip = visibleTrips[i];
        try {
          // Update progress
          setCreationProgress({
            current: i + 1,
            total: visibleTrips.length,
            tripName: trip.suggestedName,
          });

          const startDate = fromLocalDateKey(trip.startDate);
          const endDate = fromLocalDateKey(trip.endDate);

          const tripData: CreateTripData = {
            name: trip.suggestedName,
            destination: trip.location,
            startDate,
            endDate,
            travelMode: 'plane', // Default, can be improved
            emailSyncEnabled: true,
          };

          const tripId = await createTrip(firebaseUser.uid, tripData);
          newTripIds.push(tripId);
          setItinerarySyncStatus(prev => ({ ...prev, [tripId]: 'pending' }));

          // Store bookings directly from detected trip (immediate availability)
          if (trip.bookings && trip.bookings.length > 0) {
            try {
              setItinerarySyncStatus(prev => ({ ...prev, [tripId]: 'syncing' }));
              const { storeDetectedBookings } = await import('@/services/bookings');
              await storeDetectedBookings(tripId, firebaseUser.uid, trip.bookings, provider, {
                autoExpandTripDates: false,
              });
              console.log(`[EmailSyncOnboarding] Stored ${trip.bookings.length} detected bookings`);
              // Itinerary items will be created automatically via Firestore trigger
              // Set status to complete after a short delay to allow trigger to process
              setTimeout(() => {
                setItinerarySyncStatus(prev => ({ ...prev, [tripId]: 'complete' }));
              }, 2000);
            } catch (storeError) {
              const message = storeError instanceof Error ? storeError.message : String(storeError);
              console.warn('[EmailSyncOnboarding] Failed to store detected bookings', { message });
              setItinerarySyncStatus(prev => ({ ...prev, [tripId]: 'complete' })); // Still mark as complete, syncInbox will try
            }
          } else {
            setItinerarySyncStatus(prev => ({ ...prev, [tripId]: 'complete' }));
          }

          // Sync inbox to import any additional bookings for this trip
          try {
            await syncInbox(tripId, provider);
          } catch (syncError) {
            const message = syncError instanceof Error ? syncError.message : String(syncError);
            console.warn('[EmailSyncOnboarding] Failed to sync inbox for created trip', { message });
            // Continue even if sync fails
          }

          successCount++;
        } catch (err) {
          console.error(`[EmailSyncOnboarding] Error creating trip:`, err);
          if (isTripLimitEntitlementError(err)) {
            tripLimitReached = true;
            const tripLimitMessage = 'Free plan allows 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to continue.';
            newFailedTrips.push({ trip, error: tripLimitMessage });
            openPremiumGate({
              feature: 'trip_limit',
              title: '1 active trip on Free',
              message: tripLimitMessage,
              source: 'feature_gate',
            });
            break;
          }
          failCount++;
          const errorMessage = humanizeAsyncError(err, 'We couldn’t add this trip yet.');
          newFailedTrips.push({ trip, error: errorMessage });
        }
      }

      setCreatedTripIds(newTripIds);
      setFailedTrips(newFailedTrips);
      setCreationProgress(null);
      void logAnalyticsEvent('email_sync_sync_completed', {
        provider,
        source: 'onboarding',
        result: failCount > 0 || tripLimitReached ? 'failed' : 'success',
        bookingsCount: visibleTrips.reduce((count, trip) => count + (trip.bookings?.length ?? 0), 0),
        tripsCreated: successCount,
        error: failCount > 0 || tripLimitReached ? 'trip_creation_partial_or_failed' : undefined,
      });

      // Remove successfully created trips from detectedTrips to prevent duplicates
      // Use a more robust comparison: match by name, dates, and location
      if (successCount > 0) {
        setDetectedTrips(prev => {
          const remaining = prev.filter(trip => {
            // Check if this trip matches any successfully created trip
            return !visibleTrips.some(createdTrip => {
              // Match by name, start date, end date, and location
              return createdTrip.suggestedName === trip.suggestedName &&
                     createdTrip.startDate === trip.startDate &&
                     createdTrip.endDate === trip.endDate &&
                     createdTrip.location === trip.location;
            });
          });
          console.log(`[EmailSyncOnboarding] Removed ${prev.length - remaining.length} created trips from detected list`);
          return remaining;
        });
      }

      if (tripLimitReached) {
        const errorMsg = successCount > 0 ?
          `Created ${successCount} trip${successCount !== 1 ? 's' : ''}. Upgrade to create more active trips.` :
          'Free plan allows 1 active trip. Upgrade to create more.';
        setError(errorMsg);
        showError(errorMsg);
        if (successCount > 0) {
          setTripsCreated(true);
          setResultsCollapsed(true);
          setShowSuccessModal(true);
        }
      } else if (failCount > 0) {
        const errorMsg = `Created ${successCount} trip${successCount !== 1 ? 's' : ''}, ${failCount} failed`;
        setError(errorMsg);
        showError(errorMsg);
        // Still show success modal if at least one trip was created
        if (successCount > 0) {
          setTripsCreated(true);
          setResultsCollapsed(true);
          setShowSuccessModal(true);
        }
      } else {
        setTripsCreated(true);
        setResultsCollapsed(true);
        setShowSuccessModal(true);
        showSuccess(`Successfully created ${successCount} trip${successCount !== 1 ? 's' : ''}!`);
      }
    } catch (err) {
      console.error('[EmailSyncOnboarding] Error creating trips:', err);
      void logAnalyticsEvent('email_sync_sync_completed', {
        provider: selectedProvider || 'gmail',
        source: 'onboarding',
        result: 'failed',
        bookingsCount: visibleTrips.reduce((count, trip) => count + (trip.bookings?.length ?? 0), 0),
        tripsCreated: 0,
        error: err instanceof Error ? err.message : String(err),
      });
      const friendlyError = humanizeAsyncError(err, 'We couldn’t create trips right now. Please try again.');
      setError(friendlyError);
      showError(friendlyError);
    } finally {
      setCreatingTrips(false);
      setCreationProgress(null);
    }
  };

  const handleContinue = async () => {
    if (completingOnboarding) {
      return;
    }
    setCompletingOnboarding(true);
    try {
      await finishOnboardingAndGoToTabs();
    } finally {
      setCompletingOnboarding(false);
    }
  };

  const handleCreateSingleTrip = async (trip: DetectedTrip) => {
    if (!firebaseUser?.uid) {
      return;
    }

    setCreatingTrips(true);
    setError(null);

    try {
      const provider = selectedProvider || 'gmail';
      const startDate = fromLocalDateKey(trip.startDate);
      const endDate = fromLocalDateKey(trip.endDate);

      setCreationProgress({
        current: 1,
        total: 1,
        tripName: trip.suggestedName,
      });

      const tripData: CreateTripData = {
        name: trip.suggestedName,
        destination: trip.location,
        startDate,
        endDate,
        travelMode: 'plane', // Default, can be improved
        emailSyncEnabled: true,
      };

      const tripId = await createTrip(firebaseUser.uid, tripData);
      setCreatedTripIds([tripId]);
      setItinerarySyncStatus({ [tripId]: 'pending' });

      // Store bookings directly from detected trip (immediate availability)
      if (trip.bookings && trip.bookings.length > 0) {
        try {
          setItinerarySyncStatus({ [tripId]: 'syncing' });
          const { storeDetectedBookings } = await import('@/services/bookings');
          await storeDetectedBookings(tripId, firebaseUser.uid, trip.bookings, provider, {
            autoExpandTripDates: false,
          });
          console.log(`[EmailSyncOnboarding] Stored ${trip.bookings.length} detected bookings`);
          // Itinerary items are now created immediately for bookings with clear dates
          // Firestore trigger handles bookings without dates
          setItinerarySyncStatus({ [tripId]: 'complete' });
        } catch (storeError) {
          const message = storeError instanceof Error ? storeError.message : String(storeError);
          console.warn('[EmailSyncOnboarding] Failed to store detected bookings', { message });
          setItinerarySyncStatus({ [tripId]: 'complete' }); // Still mark as complete
        }
      } else {
        setItinerarySyncStatus({ [tripId]: 'complete' });
      }

      // Sync inbox to import any additional bookings for this trip
      try {
        await syncInbox(tripId, provider);
      } catch (syncError) {
        const message = syncError instanceof Error ? syncError.message : String(syncError);
        console.warn('[EmailSyncOnboarding] Failed to sync inbox for created trip', { message });
        // Continue even if sync fails
      }

      // Remove the created trip from the list using robust comparison
      // Match by name, dates, and location to handle duplicates
      setDetectedTrips(prev => prev.filter(t => {
        return !(t.suggestedName === trip.suggestedName &&
                 t.startDate === trip.startDate &&
                 t.endDate === trip.endDate &&
                 t.location === trip.location);
      }));
      
      // Show success with navigation options
      showSuccess(`Trip "${trip.suggestedName}" created successfully!`);
      setTripsCreated(true);
      setResultsCollapsed(true);
      setShowSuccessModal(true);
      void logAnalyticsEvent('email_sync_sync_completed', {
        provider,
        source: 'onboarding',
        result: 'success',
        bookingsCount: trip.bookings?.length ?? 0,
        tripsCreated: 1,
      });
      
      // If this was the last trip, auto-continue after showing modal
      if (detectedTrips.length === 1) {
        // Don't auto-navigate, let user choose from success modal
      }
    } catch (err) {
      console.error('[EmailSyncOnboarding] Error creating trip:', err);
      void logAnalyticsEvent('email_sync_sync_completed', {
        provider: selectedProvider || 'gmail',
        source: 'onboarding',
        result: 'failed',
        bookingsCount: trip.bookings?.length ?? 0,
        tripsCreated: 0,
        error: err instanceof Error ? err.message : String(err),
      });
      if (isTripLimitEntitlementError(err)) {
        const tripLimitMessage = 'Free plan allows 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to continue.';
        openPremiumGate({
          feature: 'trip_limit',
          title: '1 active trip on Free',
          message: tripLimitMessage,
          source: 'feature_gate',
        });
        setError(tripLimitMessage);
        showError(tripLimitMessage);
        return;
      }
      const errorMessage = humanizeAsyncError(err, 'We couldn’t add this trip yet. Please try again.');
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setCreatingTrips(false);
      setCreationProgress(null);
    }
  };

  const [expandedTrips, setExpandedTrips] = useState<Set<number>>(new Set());

  const toggleTripExpansion = (index: number) => {
    const newExpanded = new Set(expandedTrips);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedTrips(newExpanded);
  };

  const dismissTrip = (index: number) => {
    const newDismissed = new Set(dismissedTrips);
    newDismissed.add(index);
    setDismissedTrips(newDismissed);
  };

  const undismissTrip = (index: number) => {
    const newDismissed = new Set(dismissedTrips);
    newDismissed.delete(index);
    setDismissedTrips(newDismissed);
  };

  // Filter out dismissed trips for display
  const visibleTrips = detectedTrips.filter((_, index) => !dismissedTrips.has(index));

  const toggleTripSelection = (index: number) => {
    console.log('[EmailSync] Toggle selection called for index:', index);
    if (mergeError) {
      setMergeError(null);
    }
    setSelectedTripsForMerge(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(index)) {
        console.log('[EmailSync] Removing index from selection');
        newSelected.delete(index);
      } else {
        console.log('[EmailSync] Adding index to selection');
        newSelected.add(index);
      }
      console.log('[EmailSync] New selection:', Array.from(newSelected));
      return newSelected;
    });
  };

  const mergeSelectedTrips = () => {
    if (selectedTripsForMerge.size < 2) {
      setMergeError('Select at least two trips to merge.');
      return;
    }

    const selectedIndices = Array.from(selectedTripsForMerge).sort((a, b) => a - b);
    const tripsToMerge = selectedIndices.map(index => detectedTrips[index]);
    
    // Combine all bookings
    const allBookings = tripsToMerge.flatMap(trip => trip.bookings || []);
    
    // Find earliest start date and latest end date
    const allStartDates = tripsToMerge.map(t => new Date(t.startDate));
    const allEndDates = tripsToMerge.map(t => new Date(t.endDate));
    const minStartDate = new Date(Math.min(...allStartDates.map(d => d.getTime())));
    const maxEndDate = new Date(Math.max(...allEndDates.map(d => d.getTime())));
    
    // Combine locations
    const allLocations = tripsToMerge
      .map(t => t.location)
      .filter((loc): loc is string => !!loc);
    const combinedLocation = allLocations.length > 0 ? allLocations[0] : undefined;
    
    // Create merged trip name
    const mergedName = tripsToMerge.length > 1
      ? `${tripsToMerge[0].suggestedName} (Merged)`
      : tripsToMerge[0].suggestedName;
    
    // Create merged trip
    const mergedTrip: DetectedTrip = {
      suggestedName: mergedName,
      startDate: minStartDate.toISOString().split('T')[0],
      endDate: maxEndDate.toISOString().split('T')[0],
      location: combinedLocation,
      bookings: allBookings,
      confidence: Math.min(0.9, tripsToMerge.reduce((sum, t) => sum + (t.confidence || 0.5), 0) / tripsToMerge.length),
      emailSubjects: [...new Set(tripsToMerge.flatMap(t => t.emailSubjects || []))],
    };
    
    // Remove merged trips and add the merged one
    const newTrips = detectedTrips.filter((_, index) => !selectedIndices.includes(index));
    const mergedTripIndex = newTrips.length; // Index of the newly added merged trip
    newTrips.push(mergedTrip);
    setDetectedTrips(newTrips);
    
    // Preserve dismissed trips that weren't merged, and update indices
    // Remove dismissed status for trips that were merged (they're gone now)
    // Adjust indices for remaining trips
    const newDismissedTrips = new Set<number>();
    dismissedTrips.forEach((dismissedIndex) => {
      // If this dismissed trip was merged, don't preserve it
      if (selectedIndices.includes(dismissedIndex)) {
        return; // Skip - this trip was merged
      }
      // Otherwise, adjust the index based on how many trips before it were removed
      const tripsRemovedBefore = selectedIndices.filter(idx => idx < dismissedIndex).length;
      const newIndex = dismissedIndex - tripsRemovedBefore;
      // Only preserve if the new index is valid
      if (newIndex >= 0 && newIndex < newTrips.length) {
        newDismissedTrips.add(newIndex);
      }
    });
    
    // Clear selection
    setSelectedTripsForMerge(new Set());
    setDismissedTrips(newDismissedTrips);
    setMergeError(null);
    setError(null);
    showSuccess(`Merged ${tripsToMerge.length} trips into "${mergedName}"`);
  };

  const getBookingTypeIcon = (type: string): keyof typeof Ionicons.glyphMap => {
    switch (type) {
      case 'flight': return 'airplane';
      case 'hotel': return 'bed';
      case 'car': return 'car';
      case 'restaurant': return 'restaurant';
      case 'activity': return 'ticket';
      default: return 'calendar';
    }
  };

  const sanitizeProvider = (value?: string) => {
    if (!value) return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    const lower = trimmed.toLowerCase();
    if (['your', 'unknown provider', 'unknown airline', 'klook activity', 'activity'].includes(lower)) {
      return '';
    }
    return trimmed;
  };

  const getBookingDisplayName = (booking: ExtractedBooking) => {
    const provider = sanitizeProvider(booking.provider);
    if (provider) return provider;
    if (booking.location && booking.location !== 'unknown') return booking.location;
    if (booking.rawEmailSubject) return booking.rawEmailSubject.slice(0, 60);
    return 'Booking';
  };

  const formatDateSafe = (dateString?: string, fmt: string = 'MMM d, yyyy') => {
    if (!dateString) return '';
    const d = fromLocalDateKey(dateString);
    if (isNaN(d.getTime())) return '';
    return format(d, fmt);
  };

  const renderTripPreview = (trip: DetectedTrip, originalIndex: number, displayIndex: number) => {
    const startDate = fromLocalDateKey(trip.startDate);
    const endDate = fromLocalDateKey(trip.endDate);
    const isCreatingThisTrip = creatingTrips && detectedTrips[originalIndex] === trip;
    const isExpanded = expandedTrips.has(originalIndex);
    const isDismissed = dismissedTrips.has(originalIndex);
    
    if (isDismissed) {
      return (
        <Card key={originalIndex} variant="outlined" style={{ marginBottom: theme.spacing(1.5), opacity: 0.5 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1.5) }}>
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
              <Body color={theme.colors.textSecondary} style={{ textDecorationLine: 'line-through' }}>
                {trip.suggestedName}
              </Body>
            </View>
            <Pressable
              onPress={() => undismissTrip(originalIndex)}
              style={{ padding: theme.spacing(0.5) }}
            >
              <Caption style={{ color: theme.colors.primary }}>Undo</Caption>
            </Pressable>
          </View>
        </Card>
      );
    }

    const isSelected = selectedTripsForMerge.has(originalIndex);

    return (
      <Card 
        key={originalIndex} 
        variant={isSelected ? "filled" : "outlined"} 
        style={{ 
          marginBottom: theme.spacing(1.5),
          borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
          borderWidth: isSelected ? 2 : 1,
        }}
      >
        <View style={{ gap: theme.spacing(1.5) }}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(1.5) }}>
            {/* Selection checkbox - make it pressable separately with better visibility */}
            <Pressable
              onPress={() => {
                console.log('[EmailSync] Toggling trip selection:', originalIndex, 'currently selected:', isSelected);
                toggleTripSelection(originalIndex);
              }}
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                borderWidth: 2,
                borderColor: isSelected 
                  ? theme.colors.primary 
                  : theme.colors.textSecondary || '#888',
                backgroundColor: isSelected 
                  ? theme.colors.primary 
                  : theme.colors.surface || 'rgba(255, 255, 255, 0.1)',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 2,
                shadowColor: isSelected ? theme.colors.primary : 'transparent',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: isSelected ? 0.3 : 0,
                shadowRadius: 4,
                elevation: isSelected ? 3 : 0,
              }}
            >
              {isSelected && (
                <Ionicons name="checkmark" size={18} color={theme.colors.surface || '#FFFFFF'} />
              )}
            </Pressable>
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: theme.radii.md,
                backgroundColor: theme.colors.primary + '20',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
                {/* Use booking type icon - prefer hotel/activity over flight */}
                {(() => {
                  // Get primary booking type from trip bookings
                  // DetectedTrip bookings carry string dates; accept that shape here
                  const getPrimaryBookingType = (
                    bookings: DetectedTrip["bookings"],
                  ): ExtractedBooking["type"] => {
                    if (!bookings || bookings.length === 0) return 'other';
                    
                    // Count occurrences of each type
                    const typeCounts: Record<string, number> = {};
                    bookings.forEach(b => {
                      const type = b.type || 'other';
                      typeCounts[type] = (typeCounts[type] || 0) + 1;
                    });
                    
                    // Priority order: hotel > activity > restaurant > car > flight > other
                    const priority = ['hotel', 'activity', 'restaurant', 'car', 'flight', 'other'];
                    
                    for (const type of priority) {
                      if (typeCounts[type] && typeCounts[type] > 0) {
                        return type as ExtractedBooking["type"];
                      }
                    }
                    
                    return 'other';
                  };
                  
                  const iconType = getPrimaryBookingType(trip.bookings || []);
                  return <Ionicons name={getBookingTypeIcon(iconType)} size={20} color={theme.colors.primary} />;
                })()}
            </View>
            <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
              <SubHeading>{trip.suggestedName}</SubHeading>
              {trip.location && (
                <Caption color={theme.colors.textSecondary}>
                  <Ionicons name="location-outline" size={12} /> {trip.location}
                </Caption>
              )}
              <Caption color={theme.colors.textSecondary}>
                {formatDateSafe(trip.startDate, 'MMM d')} - {formatDateSafe(trip.endDate, 'MMM d, yyyy')}
              </Caption>
              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                {trip.bookings.length} booking{trip.bookings.length !== 1 ? 's' : ''} found
              </Caption>
            </View>
            <View
              style={{
                paddingHorizontal: theme.spacing(1),
                paddingVertical: theme.spacing(0.5),
                borderRadius: theme.radii.sm,
                backgroundColor: trip.confidence > 0.7 ? theme.colors.success + '20' : theme.colors.warning + '20',
              }}
            >
              <Caption
                color={trip.confidence > 0.7 ? theme.colors.success : theme.colors.warning}
                style={{ fontSize: 10, fontWeight: '600' }}
              >
                {Math.round(trip.confidence * 100)}% match
              </Caption>
            </View>
          </View>

          {/* Expandable bookings section */}
          {(trip.emailSubjects && trip.emailSubjects.length > 0) || (trip.bookings && trip.bookings.length > 0) ? (
            <Pressable
              onPress={() => toggleTripExpansion(originalIndex)}
              style={{ marginTop: theme.spacing(0.5) }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons
                  name={isExpanded ? 'chevron-up' : 'chevron-down'}
                  size={16}
                  color={theme.colors.primary}
                />
                <Caption style={{ color: theme.colors.primary }}>
                  {isExpanded ? 'Hide' : 'View'} {trip.bookings?.length || 0} booking{trip.bookings?.length !== 1 ? 's' : ''} ({trip.emailSubjects?.length || 0} email{trip.emailSubjects?.length !== 1 ? 's' : ''})
                </Caption>
              </View>
            </Pressable>
          ) : null}

          {isExpanded && (
            <View style={{ marginTop: theme.spacing(1), padding: theme.spacing(1.5), backgroundColor: theme.colors.background, borderRadius: theme.radii.md }}>
              {/* Email Subjects */}
              {trip.emailSubjects && trip.emailSubjects.length > 0 && (
                <View style={{ marginBottom: theme.spacing(1.5) }}>
                  <Body style={{ fontWeight: '600', marginBottom: theme.spacing(0.5), fontSize: 13 }}>
                    Email Subjects:
                  </Body>
                  {trip.emailSubjects.map((subject, subjectIndex) => (
                    <View
                      key={subjectIndex}
                      style={{
                        padding: theme.spacing(1),
                        marginBottom: theme.spacing(0.5),
                        backgroundColor: theme.colors.surface,
                        borderRadius: theme.radii.sm,
                      }}
                    >
                      <Caption style={{ fontSize: 11 }}>{subject}</Caption>
                    </View>
                  ))}
                </View>
              )}

              {/* Booking Details */}
              {trip.bookings && trip.bookings.length > 0 && (
                <View>
                  <Body style={{ fontWeight: '600', marginBottom: theme.spacing(0.5), fontSize: 13 }}>
                    Booking Details:
                  </Body>
                  {trip.bookings.map((booking, bookingIndex) => (
                    <View
                      key={bookingIndex}
                      style={{
                        padding: theme.spacing(1.5),
                        marginBottom: theme.spacing(1),
                        backgroundColor: theme.colors.surface,
                        borderRadius: theme.radii.sm,
                        borderLeftWidth: 3,
                        borderLeftColor: theme.colors.primary,
                      }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), marginBottom: theme.spacing(0.5) }}>
                        <Ionicons
                          name={getBookingTypeIcon(booking.type || 'other')}
                          size={16}
                          color={theme.colors.primary}
                        />
                        <Body style={{ fontWeight: '600', textTransform: 'capitalize', fontSize: 12 }}>
                          {booking.type || 'other'} - {getBookingDisplayName(booking as ExtractedBooking)}
                        </Body>
                      </View>
                      {booking.rawEmailSubject && (
                        <Caption style={{ fontSize: 10, marginTop: theme.spacing(0.5), fontStyle: 'italic' }}>
                          {booking.rawEmailSubject}
                        </Caption>
                      )}
                      {booking.confirmationNumber && (
                        <Caption style={{ fontSize: 10, marginTop: theme.spacing(0.5) }}>
                          Confirmation: {booking.confirmationNumber}
                        </Caption>
                      )}
                      {booking.location && (
                        <Caption style={{ fontSize: 10, marginTop: theme.spacing(0.5) }}>
                          📍 {booking.location}
                        </Caption>
                      )}
                      {(booking.departureDate || booking.checkInDate) && (
                        <Caption style={{ fontSize: 10, marginTop: theme.spacing(0.5) }}>
                          {booking.type === 'flight' && booking.departureDate
                            ? `Departure: ${formatDateSafe(booking.departureDate)}`
                            : booking.type === 'hotel' && booking.checkInDate
                              ? `Check-in: ${formatDateSafe(booking.checkInDate)}`
                              : booking.departureDate
                                ? `Date: ${formatDateSafe(booking.departureDate)}`
                                : booking.checkInDate
                                  ? `Start: ${formatDateSafe(booking.checkInDate)}`
                                  : null}
                          {booking.type === 'flight' && booking.arrivalDate && ` - Arrival: ${formatDateSafe(booking.arrivalDate)}`}
                          {booking.type === 'hotel' && booking.checkOutDate && ` - Check-out: ${formatDateSafe(booking.checkOutDate)}`}
                          {booking.type !== 'flight' && booking.type !== 'hotel' && booking.arrivalDate && ` - End: ${formatDateSafe(booking.arrivalDate)}`}
                        </Caption>
                      )}
                    </View>
                  ))}
                </View>
              )}
            </View>
          )}

          {/* Action Buttons */}
          <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
            <Button
              label={isCreatingThisTrip ? "Creating..." : "Create Trip"}
              onPress={() => handleCreateSingleTrip(trip)}
              disabled={creatingTrips || !firebaseUser?.uid}
              variant="outlined"
              size="small"
              style={{ flex: 1 }}
              icon={isCreatingThisTrip ? undefined : <Ionicons name="add-circle-outline" size={16} color={theme.colors.primary} />}
            />
            <Pressable
              onPress={() => dismissTrip(originalIndex)}
              style={{
                paddingHorizontal: theme.spacing(1.5),
                paddingVertical: theme.spacing(1),
                borderRadius: theme.radii.md,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Ionicons name="close-outline" size={18} color={theme.colors.textSecondary} />
            </Pressable>
          </View>
        </View>
      </Card>
    );
  };

  // Protection: Don't render if returning user (avoid flashing onboarding chrome during an active transition)
  if (
    authReady &&
    !isNewUser &&
    hasCompletedOnboarding &&
    !showDetectedTripsPreview &&
    (connecting !== null || detecting || isDetectingTrips)
  ) {
    return (
      <View style={{ flex: 1, width: '100%', alignSelf: 'stretch' }}>
        <LoadingState variant="fullscreen" message="Opening your trips..." />
      </View>
    );
  }

  const inboxPreviewUserId = firebaseUser?.uid ?? authUid;

  if (connecting !== null && !showDetectedTripsPreview) {
    return (
      <View style={{ flex: 1, width: '100%', alignSelf: 'stretch', backgroundColor: theme.colors.background }}>
        <LoadingState
          variant="fullscreen"
          message={`Finishing ${PROVIDER_LABELS[connecting]} connection…`}
        />
        <View
          style={{
            position: 'absolute',
            left: theme.spacing(3),
            right: theme.spacing(3),
            bottom: theme.spacing(8),
            alignItems: 'center',
          }}
        >
          <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            This can take up to ~15 seconds while we verify inbox access.
          </Caption>
        </View>
      </View>
    );
  }

  if (showDetectedTripsPreview && selectedProvider && !inboxPreviewUserId) {
    return (
      <View
        style={{
          flex: 1,
          width: '100%',
          alignSelf: 'stretch',
          backgroundColor: theme.colors.background,
        }}
      >
        <LoadingState variant="fullscreen" message="Finishing sign-in…" />
      </View>
    );
  }

  if (
    showDetectedTripsPreview &&
    selectedProvider &&
    inboxPreviewUserId &&
    useUnifiedDetectedTrips
  ) {
    return (
      <View
        style={{
          flex: 1,
          width: '100%',
          alignSelf: 'stretch',
          minHeight: 0,
          backgroundColor: theme.colors.background,
        }}
      >
        <DetectedTripsPreview
          presentation="embedded"
          visible
          initialTaskId={
            minimizedDetectionSession?.provider === selectedProvider
              ? minimizedDetectionSession.taskId
              : undefined
          }
          onClose={() => {
            oauthPreviewOpenedRef.current = false;
            setShowDetectedTripsPreview(false);
            setSelectedProvider(null);
            setMinimizedDetectionSession(null);
          }}
          onMinimize={(session) => {
            setMinimizedDetectionSession(session);
            setSelectedProvider(session.provider);
            setShowDetectedTripsPreview(false);
          }}
          onTripsCreated={() => {
            setTripsCreated(true);
            setShowDetectedTripsPreview(false);
            setSelectedProvider(null);
            setMinimizedDetectionSession(null);
            showSuccess('Trips imported successfully!');
          }}
          userId={inboxPreviewUserId}
          provider={selectedProvider}
          showEmailSubjects={false}
        />
      </View>
    );
  }

  return (
    <Screen
      title="Import your trips automatically"
      subtitle="Optional: Connect your email to automatically import trips from booking confirmations"
      scrollable
      showBackButton={true}
      onBack={() => router.replace('/(onboarding)/first-trip')}
    >
      <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
        Optional — you can connect email anytime from Trips.
      </Caption>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ gap: theme.spacing(3) }}
        showsVerticalScrollIndicator={false}
      >
        {/* Value Proposition */}
        <Card>
          <View style={{ gap: theme.spacing(1.5) }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              <Ionicons name="mail-outline" size={24} color={theme.colors.primary} />
              <SubHeading>We'll find flights, hotels, and reservations you've already booked</SubHeading>
            </View>
            <Body color={theme.colors.textSecondary}>
              We scan your email for flight, hotel, and activity confirmations, then automatically create trips with all your bookings organized.
            </Body>
            <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5), lineHeight: 20 }}>
              {emailScanLookbackExplanation(EMAIL_SYNC_ONBOARDING_LOOKBACK_DAYS)}
            </Caption>
            <View style={{ gap: theme.spacing(1), marginTop: theme.spacing(1) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                <Ionicons name="checkmark-circle" size={18} color={theme.colors.success} />
                <Caption color={theme.colors.textSecondary}>Only scans booking emails you authorize; disconnect anytime</Caption>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                <Ionicons name="checkmark-circle" size={18} color={theme.colors.success} />
                <Caption color={theme.colors.textSecondary}>Only reads booking emails</Caption>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                <Ionicons name="checkmark-circle" size={18} color={theme.colors.success} />
                <Caption color={theme.colors.textSecondary}>Works with Gmail, Outlook, iCloud</Caption>
              </View>
            </View>
          </View>
        </Card>

        {/* Preview Section */}
        <Card variant="outlined">
          <View style={{ gap: theme.spacing(1.5) }}>
            <SubHeading>We'll scan for emails from:</SubHeading>
            <View style={{ gap: theme.spacing(1) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5) }}>
                <Ionicons name="airplane-outline" size={20} color={theme.colors.primary} />
                <Body color={theme.colors.textSecondary}>Airlines (United, Delta, American, etc.)</Body>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5) }}>
                <Ionicons name="bed-outline" size={20} color={theme.colors.primary} />
                <Body color={theme.colors.textSecondary}>Hotels (Marriott, Hilton, Booking.com, etc.)</Body>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5) }}>
                <Ionicons name="ticket-outline" size={20} color={theme.colors.primary} />
                <Body color={theme.colors.textSecondary}>Booking sites (Airbnb, Expedia, etc.)</Body>
              </View>
            </View>
          </View>
        </Card>

        {/* Trip Preview */}
        {detecting && (
          <Card>
            <View style={{ gap: theme.spacing(1.5) }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(2) }}>
              <ActivityIndicator size="small" color={theme.colors.primary} />
                <View style={{ flex: 1 }}>
                  <Body color={theme.colors.textSecondary}>
                    {detectionProgress?.message || 'Scanning your email for trips...'}
                  </Body>
                  {detectionProgress && (
                    <View style={{ marginTop: theme.spacing(1) }}>
                      {/* Progress Bar */}
                      <View
                        style={{
                          height: 4,
                          backgroundColor: theme.colors.border,
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <View
                          style={{
                            height: '100%',
                            width: `${detectionProgress.progress}%`,
                            backgroundColor: theme.colors.primary,
                            borderRadius: 2,
                          }}
                        />
                      </View>
                      {/* Progress Percentage */}
                      <Caption
                        color={theme.colors.textSecondary}
                        style={{ marginTop: theme.spacing(0.5), textAlign: 'right' }}
                      >
                        {detectionProgress.progress}%
                      </Caption>
                    </View>
                  )}
                </View>
              </View>
              {!detectionProgress && (
                <Caption color={theme.colors.textSecondary} style={{ marginLeft: 28 }}>
                  This may take a few moments. We're analyzing your booking emails in parallel for faster processing.
                </Caption>
              )}
            </View>
          </Card>
        )}

        {/* Creation Progress */}
        {creatingTrips && creationProgress && (
          <Card variant="outlined" style={{ borderColor: theme.colors.primary }}>
            <View style={{ gap: theme.spacing(1.5) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(2) }}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <View style={{ flex: 1 }}>
                  <Body>
                    Creating trip {creationProgress.current} of {creationProgress.total}...
                  </Body>
                  {creationProgress.tripName && (
                    <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                      {creationProgress.tripName}
                    </Caption>
                  )}
                </View>
              </View>
              {/* Progress Bar */}
              <View
                style={{
                  height: 4,
                  backgroundColor: theme.colors.border,
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <View
                  style={{
                    height: '100%',
                    width: `${(creationProgress.current / creationProgress.total) * 100}%`,
                    backgroundColor: theme.colors.primary,
                    borderRadius: 2,
                  }}
                />
              </View>
            </View>
          </Card>
        )}

        {/* Itinerary Sync Status */}
        {tripsCreated && Object.keys(itinerarySyncStatus).length > 0 && (
          <Card variant="outlined">
            <View style={{ gap: theme.spacing(1) }}>
              <SubHeading>Processing Itinerary Items</SubHeading>
              {Object.entries(itinerarySyncStatus).map(([tripId, status]) => {
                const trip = detectedTrips.find(t => {
                  // Find trip by matching name or other identifier
                  // Since we don't have tripId in DetectedTrip, we'll show generic status
                  return true;
                });
                return (
                  <View key={tripId} style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                    {status === 'syncing' ? (
                      <ActivityIndicator size="small" color={theme.colors.primary} />
                    ) : status === 'complete' ? (
                      <Ionicons name="checkmark-circle" size={16} color={theme.colors.success} />
                    ) : (
                      <Ionicons name="time-outline" size={16} color={theme.colors.textSecondary} />
                    )}
                    <Caption color={theme.colors.textSecondary}>
                      {status === 'syncing' 
                        ? 'Adding bookings to itinerary...' 
                        : status === 'complete' 
                          ? 'Itinerary items created' 
                          : 'Waiting to process...'}
                    </Caption>
                  </View>
                );
              })}
            </View>
          </Card>
        )}

        {/* Failed Trips with Retry */}
        {failedTrips.length > 0 && (
          <Card variant="outlined" style={{ borderColor: theme.colors.error }}>
            <View style={{ gap: theme.spacing(1.5) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                <Ionicons name="alert-circle" size={20} color={theme.colors.error} />
                <SubHeading style={{ color: theme.colors.error }}>
                  {failedTrips.length} Trip{failedTrips.length !== 1 ? 's' : ''} Failed
                </SubHeading>
              </View>
              {failedTrips.map(({ trip, error }, index) => (
                <View key={index} style={{ padding: theme.spacing(1.5), backgroundColor: theme.colors.background, borderRadius: theme.radii.md }}>
                  <Body style={{ fontWeight: '600', marginBottom: theme.spacing(0.5) }}>
                    {trip.suggestedName}
                  </Body>
                  <Caption color={theme.colors.error} style={{ marginBottom: theme.spacing(1) }}>
                    {error}
                  </Caption>
                  <Button
                    label="Retry"
                    onPress={() => handleCreateSingleTrip(trip)}
                    variant="outlined"
                    size="small"
                    disabled={creatingTrips}
                  />
                </View>
              ))}
            </View>
          </Card>
        )}

        {!useUnifiedDetectedTrips && !detecting && detectedTrips.length > 0 && !resultsCollapsed && (
          <Card>
            <View style={{ marginBottom: theme.spacing(1.5) }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(1) }}>
              <SubHeading>Found {detectedTrips.length} trip{detectedTrips.length !== 1 ? 's' : ''}!</SubHeading>
              </View>
              <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                We found these trips in your email. Select multiple trips to merge them, create them now, dismiss ones that aren't trips, or review them later.
              </Body>
              {selectedTripsForMerge.size > 0 && (
                <View style={{ 
                  flexDirection: 'row', 
                  alignItems: 'center', 
                  gap: theme.spacing(1),
                  marginTop: theme.spacing(0.5),
                  padding: theme.spacing(1),
                  backgroundColor: theme.colors.primary + '15',
                  borderRadius: theme.radii.md,
                }}>
                  <Ionicons name="checkmark-circle" size={16} color={theme.colors.primary} />
                  <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
                    {selectedTripsForMerge.size} trip{selectedTripsForMerge.size !== 1 ? 's' : ''} selected for merge
                  </Caption>
                </View>
              )}
              {mergeError && (
                <Caption color={theme.colors.error} style={{ marginTop: theme.spacing(0.5) }}>
                  {mergeError}
                </Caption>
              )}
              {dismissedTrips.size > 0 && (
                <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5), fontStyle: 'italic' }}>
                  {dismissedTrips.size} trip{dismissedTrips.size !== 1 ? 's' : ''} marked as not applicable
                </Caption>
              )}
            </View>
            <View style={{ maxHeight: 400, marginBottom: theme.spacing(1.5) }}>
              <ScrollView showsVerticalScrollIndicator={false}>
                {/* Show dismissed trips first (collapsed) */}
                {detectedTrips.map((trip, originalIndex) => {
                  if (!dismissedTrips.has(originalIndex)) return null;
                  return renderTripPreview(trip, originalIndex, -1);
                })}
                
                {/* Show visible trips */}
                {visibleTrips.slice(0, 5).map((trip, displayIndex) => {
                  const originalIndex = detectedTrips.indexOf(trip);
                  return renderTripPreview(trip, originalIndex, displayIndex);
                })}
                
                {visibleTrips.length > 5 && (
                  <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', marginTop: theme.spacing(1) }}>
                    +{visibleTrips.length - 5} more trip{visibleTrips.length - 5 !== 1 ? 's' : ''}
                  </Caption>
                )}
              </ScrollView>
            </View>
          </Card>
        )}

        {/* Collapsed Results Summary */}
        {!useUnifiedDetectedTrips && resultsCollapsed && tripsCreated && (
          <Card variant="outlined" style={{ borderColor: theme.colors.success }}>
            <Pressable
              onPress={() => setResultsCollapsed(false)}
              style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
                <Body style={{ color: theme.colors.success }}>
                  {createdTripIds.length} trip{createdTripIds.length !== 1 ? 's' : ''} created successfully
              </Body>
            </View>
              <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
            </Pressable>
          </Card>
        )}


        {!useUnifiedDetectedTrips && !detecting && hasTriedDetection && detectedTrips.length === 0 && !error && (
          <Card variant="outlined">
            <View style={{ alignItems: 'center', gap: theme.spacing(1) }}>
              <Ionicons name="mail-outline" size={32} color={theme.colors.textSecondary} />
              <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
                No trips found in your email yet. Connect your email to automatically import trips as you receive booking confirmations.
              </Body>
            </View>
          </Card>
        )}

        {error && (
          <Card variant="outlined" style={{ borderColor: theme.colors.error }}>
            <View style={{ gap: theme.spacing(1.5) }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              <Ionicons name="alert-circle-outline" size={20} color={theme.colors.error} />
              <Body color={theme.colors.error} style={{ flex: 1 }}>
                {error}
              </Body>
              </View>
              {/* Retry button for detection errors */}
              {error.includes('Failed to detect') || error.includes('No trips found') || error.includes('Connection') ? (
                <Button
                  label="Try Again"
                  onPress={() => {
                    setError(null);
                    if (useUnifiedDetectedTrips) {
                      const provider = selectedProvider || 'gmail';
                      setSelectedProvider(provider);
                      setShowDetectedTripsPreview(true);
                    } else if (selectedProvider) {
                      checkForExistingTrips(selectedProvider);
                    } else {
                      checkForExistingTrips('gmail');
                    }
                  }}
                  variant="outlined"
                  size="small"
                  disabled={detecting || creatingTrips}
                />
              ) : null}
              <Button
                label="Skip and continue"
                onPress={handleContinue}
                variant="ghost"
                disabled={detecting || creatingTrips || completingOnboarding}
              />
            </View>
          </Card>
        )}

        {/* Provider Selection */}
        {!useUnifiedDetectedTrips ? (
          !hasTriedDetection || detectedTrips.length === 0 ? (
            <>
              {/* Skip button - prominent and above Connect Email */}
              <Button
                label={completingOnboarding ? 'Finishing...' : "I'll do this later"}
                onPress={handleSkip}
                disabled={detecting || connecting !== null || completingOnboarding}
                variant="outlined"
              />
              <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', marginTop: -theme.spacing(1), marginBottom: theme.spacing(1) }}>
                You can explore the app first and connect email anytime
              </Caption>
              
              <Button
                label="Connect Email"
                onPress={() => setShowProviderSelection(true)}
                disabled={connecting !== null || detecting || completingOnboarding}
                icon={<Ionicons name="mail-outline" size={18} color={theme.colors.surface} />}
              />
              
              {/* Show connection status for connected providers */}
              {Object.entries(inboxConnections).some(([_, connected]) => connected === true) && (
                <Card variant="outlined">
                  <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
                    Connected providers:
                  </Caption>
                  <View style={{ gap: theme.spacing(0.5) }}>
                    {Object.entries(inboxConnections).map(([provider, connected]) => {
                      if (connected !== true) return null;
                      return (
                        <View key={provider} style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                          <Ionicons name="checkmark-circle" size={16} color={theme.colors.success} />
                          <Caption>{PROVIDER_LABELS[provider as InboxProviderId]}</Caption>
                        </View>
                      );
                    })}
                  </View>
                </Card>
              )}
            </>
          ) : detectedTrips.length > 0 && !tripsCreated ? (
            <>
              {/* Merge button - shown when 2+ trips are selected */}
              {selectedTripsForMerge.size >= 2 && (
              <Button
                  label={`Merge ${selectedTripsForMerge.size} Trips`}
                  onPress={() => {
                    console.log('[EmailSync] Merge button pressed, selected trips:', Array.from(selectedTripsForMerge));
                    mergeSelectedTrips();
                  }}
                  variant="primary"
                  icon={<Ionicons name="git-merge-outline" size={18} color={theme.colors.onPrimary} />}
                  style={{ marginBottom: theme.spacing(1) }}
                  disabled={creatingTrips || completingOnboarding}
                />
              )}
              
              <Button
                label={
                  creatingTrips && creationProgress
                    ? `Creating ${creationProgress.current}/${creationProgress.total}...`
                    : creatingTrips
                      ? "Creating Trips..."
                      : `Create ${visibleTrips.length} Trip${visibleTrips.length !== 1 ? 's' : ''}`
                }
                onPress={handleCreateTrips}
                disabled={creatingTrips || !firebaseUser?.uid || visibleTrips.length === 0}
                icon={creatingTrips ? undefined : <Ionicons name="add-circle-outline" size={18} color={theme.colors.surface} />}
              />
              <Button
                label={completingOnboarding ? 'Finishing...' : "I'll do this later"}
                onPress={handleContinue}
                disabled={creatingTrips || completingOnboarding}
                variant="outlined"
              />
              <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', marginTop: -theme.spacing(1) }}>
                You can explore the app first and connect email anytime
              </Caption>
            </>
          ) : (
            <Button
              label={completingOnboarding ? 'Finishing...' : 'Continue'}
              onPress={handleContinue}
              disabled={detecting || creatingTrips || completingOnboarding}
            />
          )
        ) : (
          <>
            {tripsCreated ? (
              <Button
                label={completingOnboarding ? 'Finishing...' : 'Continue'}
                onPress={handleContinue}
                disabled={detecting || creatingTrips || completingOnboarding}
              />
            ) : (
              <>
                <Button
                  label={completingOnboarding ? 'Finishing...' : "I'll do this later"}
                  onPress={handleSkip}
                  disabled={detecting || connecting !== null || completingOnboarding}
                  variant="outlined"
                />
                <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', marginTop: -theme.spacing(1), marginBottom: theme.spacing(1) }}>
                  You can explore the app first and connect email anytime
                </Caption>
                <Button
                  label="Connect Email"
                  onPress={() => setShowProviderSelection(true)}
                  disabled={connecting !== null || detecting || completingOnboarding}
                  icon={<Ionicons name="mail-outline" size={18} color={theme.colors.surface} />}
                />
                {minimizedDetectionSession && (
                  <Card variant="outlined">
                    <View style={{ gap: theme.spacing(1.5) }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                        <Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />
                        <Body style={{ flex: 1 }} numberOfLines={1}>
                          {minimizedDetectionSession.label || 'Trip detection in progress'}
                        </Body>
                      </View>
                      <Caption color={theme.colors.textSecondary}>
                        Your scan is still running in the background.
                      </Caption>
                      <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                        <Button
                          label="Resume scan"
                          onPress={() => {
                            setSelectedProvider(minimizedDetectionSession.provider);
                            setShowDetectedTripsPreview(true);
                          }}
                          variant="outlined"
                          size="small"
                          style={{ flex: 1 }}
                        />
                        <Button
                          label="Dismiss"
                          onPress={() => setMinimizedDetectionSession(null)}
                          variant="ghost"
                          size="small"
                          style={{ flex: 1 }}
                        />
                      </View>
                    </View>
                  </Card>
                )}
                {Object.entries(inboxConnections).some(([_, connected]) => connected === true) && (
                  <Card variant="outlined">
                    <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
                      Connected providers:
                    </Caption>
                    <View style={{ gap: theme.spacing(0.5) }}>
                      {Object.entries(inboxConnections).map(([provider, connected]) => {
                        if (connected !== true) return null;
                        return (
                          <View key={provider} style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                            <Ionicons name="checkmark-circle" size={16} color={theme.colors.success} />
                            <Caption>{PROVIDER_LABELS[provider as InboxProviderId]}</Caption>
                          </View>
                        );
                      })}
                    </View>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </ScrollView>
      {/* Provider Selection Modal */}
      <Modal
        visible={showProviderSelection}
        animationType="slide"
        transparent
        onRequestClose={() => setShowProviderSelection(false)}
        presentationStyle="pageSheet"
      >
        <View
          style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.5)',
            justifyContent: 'flex-end',
          }}
        >
          <Pressable style={{ flex: 1 }} onPress={() => setShowProviderSelection(false)} />
          <View
            style={{
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
              padding: theme.spacing(3),
              maxHeight: '80%',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: theme.spacing(2),
              }}
            >
              <SubHeading>Choose Email Provider</SubHeading>
              <Pressable onPress={() => setShowProviderSelection(false)}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </Pressable>
            </View>

            <View style={{ gap: theme.spacing(1.5) }}>
              {(Object.keys(PROVIDER_LABELS) as InboxProviderId[]).map((provider) => {
                const isConnected = inboxConnections[provider] === true;
                const isConnecting = connecting === provider;

                return (
                  <Pressable
                    key={provider}
                    onPress={() => handleSelectProvider(provider)}
                    disabled={isConnecting}
                    style={({ pressed }) => ({
                      padding: theme.spacing(2),
                      borderRadius: theme.radii.lg,
                      borderWidth: 1,
                      borderColor: isConnected
                        ? theme.colors.success
                        : theme.colors.borderMuted,
                      backgroundColor: isConnected
                        ? theme.colors.success + '10'
                        : theme.colors.background,
                      opacity: pressed || isConnecting ? 0.7 : 1,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: theme.spacing(2),
                    })}
                  >
                    <View
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: theme.radii.md,
                        backgroundColor: theme.colors.primary + '20',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Ionicons
                        name={PROVIDER_ICONS[provider]}
                        size={24}
                        color={theme.colors.primary}
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                        <ThemedText variant="bodyStrong">{PROVIDER_LABELS[provider]}</ThemedText>
                        {isConnected && (
                          <Ionicons name="checkmark-circle" size={16} color={theme.colors.success} />
                        )}
                        {isConnecting && (
                          <ActivityIndicator size="small" color={theme.colors.primary} />
                        )}
                      </View>
                      <Caption color={theme.colors.textSecondary}>
                        {PROVIDER_DESCRIPTIONS[provider]}
                      </Caption>
                    </View>
                    <Ionicons
                      name="chevron-forward"
                      size={20}
                      color={theme.colors.textSecondary}
                    />
                  </Pressable>
                );
              })}
            </View>
          </View>
        </View>
      </Modal>

      {/* IMAP Settings Modal */}
      <ImapSettingsModal
        visible={imapModalVisible}
        onClose={() => {
          setImapModalVisible(false);
          setPendingProvider(null);
        }}
        onSave={handleImapSettingsSave}
        defaultValues={{}}
      />

      {/* Success Modal with Navigation Options */}
      <Modal
        visible={showSuccessModal}
        animationType="fade"
        transparent
        onRequestClose={() => setShowSuccessModal(false)}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: theme.spacing(3),
          }}
        >
          <Card style={{ width: '100%', maxWidth: 400 }}>
            <View style={{ alignItems: 'center', gap: theme.spacing(2), padding: theme.spacing(2) }}>
              <View
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 32,
                  backgroundColor: theme.colors.success + '20',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ionicons name="checkmark-circle" size={48} color={theme.colors.success} />
              </View>
              <SubHeading style={{ textAlign: 'center' }}>
                Trip{createdTripIds.length !== 1 ? 's' : ''} Created Successfully!
              </SubHeading>
              <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
                {createdTripIds.length} trip{createdTripIds.length !== 1 ? 's' : ''} {createdTripIds.length === 1 ? 'has' : 'have'} been created with {createdTripIds.length === 1 ? 'its' : 'their'} bookings.
                {Object.values(itinerarySyncStatus).some(s => s === 'syncing') && (
                  <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
                    Itinerary items are still being processed...
                  </Caption>
                )}
              </Body>
              <View style={{ width: '100%', gap: theme.spacing(1.5), marginTop: theme.spacing(1) }}>
                {createdTripIds.length === 1 && (
                  <Button
                    label="View Trip"
                    onPress={() => {
                      setShowSuccessModal(false);
                      router.push({
                        pathname: '/(tabs)/plan',
                        params: { tripId: createdTripIds[0] },
                      });
                    }}
                    icon={<Ionicons name="eye-outline" size={18} color={theme.colors.surface} />}
                  />
                )}
                {createdTripIds.length > 1 && (
                  <Button
                    label="View All Trips"
                    onPress={() => {
                      setShowSuccessModal(false);
                      router.push('/(tabs)');
                    }}
                    icon={<Ionicons name="list-outline" size={18} color={theme.colors.surface} />}
                  />
                )}
                <Button
                  label="Continue"
                  onPress={async () => {
                    setShowSuccessModal(false);
                    await finishOnboardingAndGoToTabs();
                  }}
                  variant="outlined"
                  icon={<Ionicons name="arrow-forward-outline" size={18} color={theme.colors.primary} />}
                />
              </View>
            </View>
          </Card>
        </View>
      </Modal>
    </Screen>
  );
}

